import React, { useEffect, useState } from "react";
import { Grid, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormField from "layouts/pages/account/components/FormField";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import SettingsCard from "./SettingsCard";

const HomepageOptinSection = ({
  inputData,
  optin_image,
  previewOptinImage,
  handleImageUpload,
  onRemoveOptinImage,
  onGhlPick,
  autoSaveField,
}) => {
  const AppService = useAppServices();
  const [agency] = useAgencyInfo();
  const isAgencyApp = !!agency?.ghl?.agency_app;
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(inputData?.optin_form_id || "");
  const [embedCode, setEmbedCode] = useState(inputData?.optin_form_embed_code || "");

  const save = (name, value) => {
    if (autoSaveField) autoSaveField(name, value);
  };
  const handleBlurSave = (e) => save(e.target.name, e.target.value);

  useEffect(() => {
    setSelectedFormId(inputData?.optin_form_id || "");
  }, [inputData?.optin_form_id]);

  useEffect(() => {
    setEmbedCode(inputData?.optin_form_embed_code || "");
  }, [inputData?.optin_form_embed_code]);

  useEffect(() => {
    const getForms = async () => {
      if (!agency?._id || !isAgencyApp) return;
      setFormsLoading(true);
      try {
        const queryParams = new URLSearchParams({ agency_id: agency._id });
        const { response } = await AppService.webhook.GetForms({
          query: queryParams.toString(),
        });
        setForms(response?.data?.data?.forms || []);
      } catch (error) {
        console.error("Error fetching GHL forms:", error);
      } finally {
        setFormsLoading(false);
      }
    };
    getForms();
  }, [agency?._id, isAgencyApp]);

  const formKey = (form) => form?._id || form?.id;

  const handleFormChange = (e) => {
    const formId = e.target.value;
    const form = forms.find((f) => formKey(f) === formId);
    setSelectedFormId(form?.id);
    save("optin_form_id", form?.id);
    save("optin_form_name", form?.name || "");
  };

  return (
    <SettingsCard
      title="Homepage Optin"
      subtitle="Configure the opt-in section shown on the home page, including a connected GoHighLevel form."
      gridProps={{
        xs: 12,
        sx: {
          "@media (min-width: 1100px)": {
            flexBasis: "calc(50% - 12px)",
            maxWidth: "calc(50% - 12px)",
            width: "calc(50% - 12px)",
            marginRight: "24px",
          },
        },
      }}
    >
      <SoftTypography fontWeight="bold" mb={1}>
        Background Image
      </SoftTypography>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <SoftBox
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            mb={0}
            sx={{ width: "100%", maxWidth: "600px" }}
          >
            <SoftBox
              sx={{
                width: "100%",
                maxWidth: "600px",
                height: "120px",
                border: "1.5px solid #e0e0e0",
                borderRadius: "12px",
                padding: "12px",
                backgroundColor: "#f9f9f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                cursor: "pointer",
                position: "relative",
                mb: 0.25,
              }}
              onClick={() => {
                if (onGhlPick) {
                  onGhlPick();
                  return;
                }
                document.getElementById("optin_image_input")?.click();
              }}
            >
              <input type="hidden" name="optin_image" value={optin_image} />
              {optin_image && onRemoveOptinImage && (
                <IconButton
                  size="small"
                  aria-label="Remove optin background image"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveOptinImage();
                  }}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    zIndex: 1,
                    backgroundColor: "rgba(0, 0, 0, 0.55)",
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.75)" },
                    width: 24,
                    height: 24,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              {previewOptinImage ? (
                <>
                  <img
                    src={previewOptinImage}
                    alt="optin_image"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                  {!onGhlPick && (
                    <input
                      id="optin_image_input"
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={(e) => {
                        handleImageUpload(e, "optin_image");
                        e.target.value = "";
                      }}
                    />
                  )}
                </>
              ) : onGhlPick ? (
                <SoftButton
                  variant="outlined"
                  color="dark"
                  onClick={(e) => {
                    e.stopPropagation();
                    onGhlPick();
                  }}
                >
                  Choose from Media
                </SoftButton>
              ) : (
                <SoftButton variant="outlined" component="label" color="dark">
                  Upload Background Image
                  <input
                    id="optin_image_input"
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={(e) => {
                      handleImageUpload(e, "optin_image");
                      e.target.value = "";
                    }}
                  />
                </SoftButton>
              )}
            </SoftBox>
            <SoftTypography
              variant="caption"
              sx={{ fontSize: "11px", color: "#666", marginTop: "6px" }}
            >
              Recommended: (1920px x 700px) 5:2 ratio. 1MB size.
            </SoftTypography>
          </SoftBox>
        </Grid>
      </Grid>

      <SoftTypography fontWeight="bold" mt={3} mb={1}>
        Optin Text
      </SoftTypography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormField
            label="Headline"
            name="optin_heading"
            defaultValue={inputData?.optin_heading || ""}
            onBlur={handleBlurSave}
          />
        </Grid>

        <Grid item xs={12}>
          <FormField
            label="Sub-headline"
            name="optin_sub_heading"
            defaultValue={inputData?.optin_sub_heading || ""}
            onBlur={handleBlurSave}
          />
        </Grid>
      </Grid>

      <SoftTypography fontWeight="bold" mt={3} mb={1}>
        Connect Form
      </SoftTypography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {isAgencyApp ? (
            <>
              <SoftTypography variant="caption" sx={{ display: "block", mb: 1, opacity: 0.75 }}>
                Select a GoHighLevel form from this sub-account to embed in the homepage optin
                section.
              </SoftTypography>
              <select
                value={selectedFormId}
                onChange={handleFormChange}
                disabled={formsLoading}
                style={{
                  width: "100%",
                  padding: "15px 10px",
                  borderRadius: "10px",
                  border: "1px solid #D2D6DA",
                }}
              >
                <option value="">
                  {formsLoading ? "Loading forms..." : "Select a form"}
                </option>
                {forms.map((form) => (
                  <option key={formKey(form)} value={formKey(form)}>
                    {form.name}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <>
              <SoftTypography variant="caption" sx={{ display: "block", mb: 1, opacity: 0.75 }}>
                Paste the form embed code (HTML, script, or iframe snippet) to display in the
                homepage optin section.
              </SoftTypography>
              <SoftInput
                multiline
                rows={6}
                value={embedCode}
                onChange={(e) => setEmbedCode(e.target.value)}
                onBlur={() => save("optin_form_embed_code", embedCode)}
                placeholder={"<!-- Paste your form embed code here -->\n<script src=\"https://...\"></script>"}
              />
            </>
          )}
        </Grid>
      </Grid>
    </SettingsCard>
  );
};

export default HomepageOptinSection;
